/**
 * NotificationPreferences Component
 *
 * Settings page section for managing notification preferences.
 * Allows users to configure channel preferences and notification types.
 *
 * Features:
 * - Channel toggles (in-app, email, SMS, push)
 * - Notification type toggles (booking, payment, message, etc.)
 * - Save button with loading state
 * - Reset to defaults button
 * - Success/error feedback
 * - Organized sections
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * @example
 * <NotificationPreferences />
 */

import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useNotificationPreferences } from '../../hooks/notifications/useNotificationPreferences';
import ConfirmModal from '../shared/ConfirmModal';

/**
 * Toggle switch component
 */
function Toggle({ enabled, onChange, label, description }) {
  return (
    <div className="flex items-start justify-between py-4">
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-900">{label}</label>
        {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
      </div>

      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`
          relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2
          ${enabled ? 'bg-brand-primary' : 'bg-gray-200'}
        `}
        role="switch"
        aria-checked={enabled}
      >
        <span
          className={`
            pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
            transition duration-200 ease-in-out
            ${enabled ? 'translate-x-5' : 'translate-x-0'}
          `}
        />
      </button>
    </div>
  );
}

/**
 * NotificationPreferences Component
 *
 * Main notification preferences management interface.
 */
export default function NotificationPreferences() {
  const {
    preferences,
    loading: _loading,
    error,
    initialized,
    updateChannels,
    updateTypes,
    resetToDefaults,
  } = useNotificationPreferences();

  const [localPreferences, setLocalPreferences] = useState(preferences);
  const [saving, setSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // ============================================================================
  // SYNC LOCAL STATE WITH HOOK
  // ============================================================================

  useEffect(() => {
    setLocalPreferences(preferences);
  }, [preferences]);

  // ============================================================================
  // DETECT CHANGES
  // ============================================================================

  useEffect(() => {
    const changed = JSON.stringify(localPreferences) !== JSON.stringify(preferences);
    setHasChanges(changed);
  }, [localPreferences, preferences]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Update channel preference
   */
  const handleChannelChange = (channel, value) => {
    setLocalPreferences((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: value,
      },
    }));
  };

  /**
   * Update type preference
   */
  const handleTypeChange = (type, value) => {
    setLocalPreferences((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        [type]: value,
      },
    }));
  };

  /**
   * Save preferences
   */
  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      // Update channels if changed
      const channelsChanged =
        JSON.stringify(localPreferences.channels) !== JSON.stringify(preferences.channels);
      if (channelsChanged) {
        await updateChannels(localPreferences.channels);
      }

      // Update types if changed
      const typesChanged =
        JSON.stringify(localPreferences.types) !== JSON.stringify(preferences.types);
      if (typesChanged) {
        await updateTypes(localPreferences.types);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('[NotificationPreferences] Failed to save:', err);
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Reset to defaults
   */
  const handleReset = () => {
    setShowResetConfirm(true);
  };

  const executeReset = async () => {
    setShowResetConfirm(false);

    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      await resetToDefaults();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('[NotificationPreferences] Failed to reset:', err);
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!initialized) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Notification Preferences</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage how and when you receive notifications from OutdoorGuides.
        </p>
      </div>

      {/* Error message */}
      {(error || saveError) && (
        <div className="mb-6 rounded-md bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error || saveError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success message */}
      {saveSuccess && (
        <div className="mb-6 rounded-md bg-green-50 p-4 border border-green-200">
          <div className="flex">
            <CheckCircleIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-800">Preferences saved successfully!</p>
            </div>
          </div>
        </div>
      )}

      {/* Channel Preferences */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Notification Channels</h3>
          <p className="text-sm text-gray-600 mt-1">Choose how you want to receive notifications.</p>
        </div>

        <div className="px-6 divide-y divide-gray-200">
          <Toggle
            enabled={localPreferences.channels.inApp}
            onChange={(value) => handleChannelChange('inApp', value)}
            label="In-App Notifications"
            description="Receive notifications within the OutdoorGuides platform."
          />

          <Toggle
            enabled={localPreferences.channels.email}
            onChange={(value) => handleChannelChange('email', value)}
            label="Email Notifications"
            description="Receive notifications via email to your registered address."
          />

          <Toggle
            enabled={localPreferences.channels.sms}
            onChange={(value) => handleChannelChange('sms', value)}
            label="SMS Notifications"
            description="Receive critical notifications via text message (SMS charges may apply)."
          />

          <Toggle
            enabled={localPreferences.channels.push}
            onChange={(value) => handleChannelChange('push', value)}
            label="Push Notifications"
            description="Receive push notifications on your mobile device."
          />
        </div>
      </div>

      {/* Notification Type Preferences */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Notification Types</h3>
          <p className="text-sm text-gray-600 mt-1">
            Choose which types of notifications you want to receive.
          </p>
        </div>

        <div className="px-6 divide-y divide-gray-200">
          <Toggle
            enabled={localPreferences.types.booking}
            onChange={(value) => handleTypeChange('booking', value)}
            label="Booking Notifications"
            description="Updates about your bookings (confirmations, cancellations, changes)."
          />

          <Toggle
            enabled={localPreferences.types.payment}
            onChange={(value) => handleTypeChange('payment', value)}
            label="Payment Notifications"
            description="Payment receipts, refunds, and transaction updates."
          />

          <Toggle
            enabled={localPreferences.types.message}
            onChange={(value) => handleTypeChange('message', value)}
            label="Message Notifications"
            description="New messages from guides or customers."
          />

          <Toggle
            enabled={localPreferences.types.review}
            onChange={(value) => handleTypeChange('review', value)}
            label="Review Notifications"
            description="New reviews and review responses."
          />

          <Toggle
            enabled={localPreferences.types.system}
            onChange={(value) => handleTypeChange('system', value)}
            label="System Notifications"
            description="Important system updates and maintenance alerts."
          />

          <Toggle
            enabled={localPreferences.types.promotional}
            onChange={(value) => handleTypeChange('promotional', value)}
            label="Promotional Notifications"
            description="Special offers, deals, and promotional content."
          />

          {/* Guide-specific types (show only for guides) */}
          {localPreferences.types.guideApproval !== undefined && (
            <Toggle
              enabled={localPreferences.types.guideApproval}
              onChange={(value) => handleTypeChange('guideApproval', value)}
              label="Guide Approval Notifications"
              description="Updates about your guide profile approval status."
            />
          )}

          {/* Admin-specific types (show only for admins) */}
          {localPreferences.types.adminAlert !== undefined && (
            <Toggle
              enabled={localPreferences.types.adminAlert}
              onChange={(value) => handleTypeChange('adminAlert', value)}
              label="Admin Alert Notifications"
              description="Important admin alerts and system issues."
            />
          )}

          {/* Support-specific types */}
          {localPreferences.types.supportTicket !== undefined && (
            <Toggle
              enabled={localPreferences.types.supportTicket}
              onChange={(value) => handleTypeChange('supportTicket', value)}
              label="Support Ticket Notifications"
              description="Updates about your support tickets."
            />
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reset to Defaults
        </button>

        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`
            px-6 py-2 text-sm font-medium text-white rounded-md
            disabled:opacity-50 disabled:cursor-not-allowed
            ${saving ? 'bg-gray-400' : 'bg-brand-primary hover:bg-brand-primary-dark'}
          `}
        >
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>

      <ConfirmModal
        isOpen={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        onConfirm={executeReset}
        title="Reset Preferences"
        message="Are you sure you want to reset all notification preferences to defaults?"
        confirmText="Reset"
        cancelText="Cancel"
        confirmButtonClass="bg-orange-600 hover:bg-orange-700"
      />
    </div>
  );
}
